/* eslint-disable @typescript-eslint/no-explicit-any */
import { BatchOperationAdd, BatchOperationUpdate, ConcurrencyError } from '@rhybus/api'
import React, { useContext, useEffect, useState } from 'react'

import { sitePath } from '../../assets/ts/api/api'
import { transactionBatch } from '../../assets/ts/data/data'
import { LIST_NAME as DUEOUT_LIST_NAME } from '../../assets/ts/data/dueout/dueout'
import { LIST_NAME as DUEOUT_DISCUSSION_LIST } from '../../assets/ts/data/dueout/dueout-discussions'
import { LIST_NAME as DUEOUT_HISTORY_LIST } from '../../assets/ts/data/dueout/dueout-history'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import {
  encodeHTML,
  formatBody,
  handleRoute,
  sendEmail,
  sendNotification,
  updateInvolvedUsers,
} from '../../assets/ts/general/general'
import { MetadataContext } from '../../store/metadata-context-provider'
import { UserContext } from '../../store/user-context-provider'
import {
  ACTION_BUTTON_POPUP_CONTROL,
  COMMENT_TYPES,
  CONCURRENCY_ERR_MSG,
  DISCUSSION_LOG,
  DUEOUTS_ACTIONS,
} from '../../utils/constants/global-constants'
import InputTextArea from '../../utils/controls/input-text-area'
import DialogBox from '../alert/dialog-box'
import ActionButtons from './action-buttons'
import Buttons from './buttons'
import './buttons.css'

interface Props {
  action: string
  actionType: string
  buttonColor: string
  currentUserRole
  data
  icon: string
  id?: any
  name: string
  popupBtnRef: React.RefObject<HTMLInputElement>
  popupClassName: string
  popupCloseOpenFunctionality: (
    type: string,
    value: number,
    top: number,
    xPosition: number,
    left: number,
    elementHeight: number,
    offsetWidth: number
  ) => void
  setRefresh: (refresh: boolean) => void
  showHide: boolean
  statusRelativeToUser: string
  style: React.CSSProperties
}

const DueoutBasicActionButton = (props: Props) => {
  const {
    action,
    buttonColor,
    currentUserRole,
    data,
    icon,
    id,
    name,
    popupBtnRef,
    popupClassName,
    popupCloseOpenFunctionality,
    setRefresh,
    showHide,
    statusRelativeToUser,
    style,
  } = props

  const { me } = useContext(UserContext)
  const { tooltipObj } = useContext(MetadataContext)
  const [userInputs, setUserInputs] = useState({
    comment: '',
  })
  const [showErrorMsgs, setShowErrorMsgs] = useState({
    comment: { show: false },
  })
  const [showConfirmReload, setShowConfirmReload] = useState<boolean>(false)
  const [tooltip, setTooltip] = useState('')

  const onChangeInputSelect = (e: any) => {
    setUserInputs({ ...userInputs, comment: e.target.value })
    setShowErrorMsgs({ ...showErrorMsgs, comment: { show: false } })
  }

  const validateInputs = () => {
    let isValid = true

    if (userInputs.comment === '' || userInputs.comment === null) {
      showErrorMsgs.comment.show = true
      isValid = false
    }

    setShowErrorMsgs({ ...showErrorMsgs })
    return isValid
  }
  const determineCommentType = (action): string => {
    if ([action].includes(['CancelResponseRequired'])) {
      return COMMENT_TYPES.Internal
    } else {
      return COMMENT_TYPES.Customer
    }
  }
  const handleSubmit = async () => {
    const isValid = validateInputs()

    if (isValid) {
      const currUser = currentUserRole?.replace('[SECTION_NAME]', data['Section/MetadataValue'])

      const itemTime = new Date()
      const userRoleForDiscussion = DISCUSSION_LOG[action]
      const dataObj: any = {
        DueOutGUID: data.DueOutGUID,
        DueOutID: data.DueOutID,
        ItemModified: itemTime,
        ItemModifiedBy: me.userId,
        ServicingBy: data.ServicingBy,
        eKey: data.eKey,
        eTag: data.eTag,
      }

      const historyObj = {
        DueOutGUID: data.DueOutGUID,
        DueOutID: data.DueOutID,
        ItemCreated: itemTime,
        ItemCreatedBy: me.userId,
        ItemModified: itemTime,
        ItemModifiedBy: me.userId,
        PreviousStatusCode: data.StatusCode,
        SectionCode: data.SectionCode,
        ServicingBy: data.ServicingBy,
        UserRole: currUser,
      }

      const discussionObj = {
        Comment: encodeHTML(userInputs.comment.trim()),
        CommentType: determineCommentType(action),
        DueOutGUID: data.DueOutGUID,
        DueOutID: data.DueOutID,
        IsActionComment: true,
        ItemCreated: itemTime,
        ItemCreatedBy: me.userId,
        ItemModified: itemTime,
        ItemModifiedBy: me.userId,
        Subject: userRoleForDiscussion,
        UserRole: currUser,
      }
      await createListObjects(
        action,
        dataObj || {},
        itemTime,
        historyObj || {},
        discussionObj || {},
        userRoleForDiscussion
      )
      try {
        const batchResult: any = await transactionBatch([
          { data: dataObj, list: DUEOUT_LIST_NAME, op: BatchOperationUpdate },
          {
            data: historyObj,
            list: DUEOUT_HISTORY_LIST,
            op: BatchOperationAdd,
          },
          {
            data: discussionObj,
            list: DUEOUT_DISCUSSION_LIST,
            op: BatchOperationAdd,
          },
        ])
        if (batchResult.result === 'PASS') {
          await handleEmails(me)
          await sendNotification(historyObj.ActionCode, data, me, currUser, false)

          setRefresh(true)
        }
      } catch (error) {
        if (error instanceof ConcurrencyError) {
          setShowConfirmReload(true)
        } else {
          console.error(error)
        }
      }
    }
  }

  useEffect(() => {
    switch (action) {
      case 'CancelDueout':
        setTooltip(tooltipObj?.cancelDueOutComment?.TooltipDescription)
        break
      case 'Respond':
        setTooltip(tooltipObj?.respondDueOutComment?.TooltipDescription)
        break
      case 'Complete':
        setTooltip(tooltipObj?.completeDueOutComment?.TooltipDescription)
        break
      case 'CloseDueout':
        setTooltip(tooltipObj?.closeDueOutComment?.TooltipDescription)
        break
    }
  }, [tooltipObj, action])

  const createListObjects = async (
    title,
    dataObj,
    itemTime,
    historyObj,
    discussionObj,
    subject
  ) => {
    const currUser = currentUserRole?.replace('[SECTION_NAME]', data['Section/MetadataValue'])
    switch (title) {
      case ACTION_BUTTON_POPUP_CONTROL.Respond:
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)
        dataObj.StatusCode = STATUS_CODE.IN_PROGRESS
        historyObj.StatusCode = STATUS_CODE.IN_PROGRESS
        historyObj.ActionCode = DUEOUTS_ACTIONS.RESPOND

        discussionObj.Subject = `${currUser}  ${subject}`
        break
      case ACTION_BUTTON_POPUP_CONTROL.Complete:
        dataObj.StatusCode = STATUS_CODE.COMPLETED
        historyObj.StatusCode = STATUS_CODE.COMPLETED
        historyObj.ActionCode = DUEOUTS_ACTIONS.COMPLETE
        if (!(data.Customer.userID === me.userId)) {
          discussionObj.Subject = `${currUser}  ${subject}`
        }
        break
      case ACTION_BUTTON_POPUP_CONTROL.CloseDueout:
        dataObj.StatusCode = STATUS_CODE.CLOSED
        historyObj.StatusCode = STATUS_CODE.CLOSED
        dataObj.ClosedDate = itemTime
        historyObj.ActionCode = DUEOUTS_ACTIONS.CLOSE
        discussionObj.Subject = `${currUser}  ${subject}`
        break
      case ACTION_BUTTON_POPUP_CONTROL.CancelDueout:
        dataObj.StatusCode = STATUS_CODE.CANCELED
        historyObj.StatusCode = STATUS_CODE.CANCELED
        historyObj.ActionCode = DUEOUTS_ACTIONS.CANCEL
        discussionObj.Subject = `${data['Section/MetadataValue']}  ${currUser}  ${subject}`
        break
    }
  }
  const resetUserInputs = () => {
    setUserInputs({
      comment: '',
    })
    setShowErrorMsgs({ ...showErrorMsgs, comment: { show: false } })
  }

  const handleEmails = async (userdetails) => {
    const userId = userdetails.userId
    const detailView = 'dueoutdetailview'
    const dataGUID = data?.DueOutGUID
    const dataID = data?.DueOutID

    const navURL = sitePath + `/${detailView}/${dataGUID}`
    let sendEmailBool = false
    let emailBody = ''

    let emailSubject = ''
    switch (action) {
      case 'Respond':
        emailSubject = `${'Due Out'} '${dataID}' - response added`
        sendEmailBool = true
        break
      case 'Complete':
        emailSubject = `Due Out '${dataID}' - has been completed`
        sendEmailBool = true
        break
      case 'CloseDueout':
        emailSubject = `${'Due Out'} '${dataID}' has been closed`
        sendEmailBool = true
        break
      case 'CancelDueout':
        emailSubject = `'Due Out '${dataID}' has been canceled`
        sendEmailBool = true
        break
    }

    if (emailBody === '') emailBody = emailSubject

    const emailBodyData = {
      ...data,
      Title: emailSubject,
      bodyText: emailBody,
      isInquiry: true,
    }

    const body = formatBody(action, emailBodyData, navURL)
    const to = data.Customer.userID

    const emailObjData = {
      ...data,
      Action: action,
      ItemGUID: dataGUID,
      ItemID: dataID,
      Title: data?.Subject,
    }

    const emails: Promise<any>[] = []
    if (sendEmailBool && to && (to !== userId.toString() || action === 'Close')) {
      emails.push(
        sendEmail({
          bcc: '',
          body: body,
          bodySurvey: '',
          cc: '',
          data: emailObjData,
          isDiscussion: false,
          isSurvey: false,
          loginuserId: userId,
          navURL: navURL,
          subject: emailSubject,
          to: to,
        })
      )
    }

    if (sendEmailBool && data?.TeamMembers && data?.TeamMembers.length > 0) {
      const teamMembers = data?.TeamMembers
      for (const teamMember of teamMembers) {
        emails.push(
          sendEmail({
            bcc: '',
            body: body,
            bodySurvey: '',
            cc: '',
            data: emailObjData,
            isDiscussion: false,
            isSurvey: false,
            loginuserId: userId,
            navURL: navURL,
            subject: emailSubject,
            to: teamMember.userID,
          })
        )
      }
    }

    await Promise.all(emails)
  }
  return (
    <div>
      <ActionButtons
        aria-label={name}
        className={`btn-lg font-12 ${buttonColor} whitetext text-nowrap border-radius poppins-bold text-uppercase`}
        icon={icon}
        id={id}
        isClick={action}
        label={name}
        name={name}
        popupCloseOpenFunctionality={popupCloseOpenFunctionality}
        showHide={showHide}
        type='button'
      />
      {showHide ? (
        <div
          className={`popup popup-md ${popupClassName}`}
          ref={popupBtnRef}
          style={style}
        >
          <div className='row'>
            <div className='col-md-12 mt-0'>
              <InputTextArea
                className='form-lg font-13 sourcesanspro  min-h-100 p-1'
                cols='30'
                error={showErrorMsgs.comment.show}
                formClassName=' form-vertical'
                info={tooltip}
                infoClassName='tool-tip tooltip-bottom-left font-12 sourcesanspro'
                infoIcon='icon-info color-primary'
                inputProps={{
                  className: 'font-12 poppins-medium title-color2 mb-1',
                  id: 'Comment',
                  name: 'Comment',
                  placeholder: 'Enter your Comment',
                }}
                isInfo
                isMandatory
                label='Comment'
                onChange={onChangeInputSelect}
                rows='3'
                type='textarea'
              />
            </div>
          </div>
          <div className='pt-2 d-flex gap-2'>
            <Buttons
              aria-label='Ok'
              className='btn btn-sm btn-primary whitetext font-11 border-radius ms-auto text-uppercase poppins-semibold'
              icon='icon-checked font-10 me-1'
              label='Ok'
              onClick={handleSubmit}
            />
            <ActionButtons
              className='btn-border1 title-color3 btn-sm font-11 border-radius text-uppercase poppins-semibold'
              icon='icon-close font-9 me-1 color-primary'
              isClick='Cancel'
              label='Cancel'
              name='Cancel'
              onClick={resetUserInputs}
              popupCloseOpenFunctionality={popupCloseOpenFunctionality}
              showhide={showHide}
              type='button'
            />
          </div>
        </div>
      ) : null}
      {showConfirmReload && (
        <DialogBox
          handleRouteNo={() => handleRoute('cancel', setShowConfirmReload)}
          handleRouteYes={handleRoute}
          message={CONCURRENCY_ERR_MSG}
          primaryText={'Ok'}
          secondaryText={'Cancel'}
        />
      )}
    </div>
  )
}

export default DueoutBasicActionButton
